<template>
<div v-bind:class="{'active': active}">
  <div class="screen-detail-pane bottom box">
    <div class="box-content" v-if="screen && screen.id">
      <div v-on:click="$emit('close')" class="caret">
        <i class="fa fa-caret-down fa-2x"></i>
      </div>
      <p class="title has-text-centered is-4">
        {{ screen.subcategory[0].name }}: {{ screen.name }}
      </p>
      <div class="columns is-vcentered">
        <div class="column is-one-fifth">
          <figure class="image is-5by3 mymargin">
            <img v-bind:src="screen.subcategory[0].image">
          </figure>
          <p class="subtitle">
            {{ screen.source_type }}
            <br>
            <span
              v-if="isVoluntarySource"
              class="self-reported"
            >
              <StakeTooltip
                label="This data is self-reported by the company and has not been independently audited or verified."
                position="right"
                trigger="hover"
              >
                <Badge
                  status="warning"
                  outlined
                  resizable
                >
                  Voluntary, unaudited
                </Badge>
              </StakeTooltip>
            </span>
            <span class="help">
              <template v-if="screen.source_link && screen.source_link.length > 1">
                {{ screen.source_name }}
                <a
                  v-for="(link, idx) in screen.source_link"
                  :key="idx"
                  target="_blank"
                  rel="noreferrer noopener"
                  :href="link.url"
                  class="source-link-number"
                >[{{ idx + 1 }}]</a>
              </template>
              <a
                v-else
                target="_blank"
                rel="noreferrer noopener"
                v-bind:href="screen.source_link[0].url"
              >
                {{ screen.source_name }}
              </a>
            </span>
            <span class="help">
                Update Frequency: {{ screen.update_frequency }}
            </span>
            
            <span class="help" v-if="screen.benchmark_description">
              Scope: {{ screen.benchmark_description }}
            </span>
            <div v-if="canDownload">
              <b-icon
                pack="fas"
                icon="circle-notch"
                class="fa-spin is-primary"
                v-show="isDownloading"
                />       
              <a @click="downloadUniverse()">Download Across All Universes</a>
            </div>
          </p>
        </div><!-- column-->
        <div class="column ">
          <div class="content has-text-left">
            <p>
              {{ screen.description }}
            </p>
            <p>
              {{ screen.long_description }}
            </p>
          </div>
        </div><!-- column -->
      </div><!--  columns -->
      <div
        v-if="companies.a.length > 0"
        class="columns is-centered"
      >
       <div class="column is-half">
         <div class="field">
             <template v-if="!screen.is_binary">
               <p class="heading is-size-7 has-text-centered">Flag the worst {{ screen.userPercentile }}% of all companies for exclusion.</p>
               <b-slider class="is-primary" :min="0" :max="100" v-model="screen.userPercentile" v-on:change="sliderChange($event, screen)">
                 <template v-for="val in [0, 20, 40, 50, 60, 80, 100]">
                     <b-slider-tick :value="val" :key="val">{{ val }}</b-slider-tick>
                 </template>
               </b-slider>
             </template>
             <template v-else>
               <div class="switch-container">
                <p class="heading is-size-7 has-text-centered">
                  Flag all companies associated with {{ screen.name }} for {{ screen.is_inclusionary ? 'Include' : 'Exclude' }}.
                </p>
                <ScreenSwitch
                  v-bind:screen="screen"
                  v-on:change="updatePercentile"
                />
              </div>
             </template>
             <!---->
         </div>
       </div>
      </div>
      <div
        v-if="companies.a.length > 0"
        id="detailedTable"
        class="container has-text-left"
      >
        <section>
          <p class="notification is-warning has-text-centered" v-show="!loading && companies.loaded && detailRecords.length === 0">
            This screen is not applicable to any companies in your current universe.
          </p>
          <p class="notification" v-show="loading">
            Loading....
          </p>
         <b-table
           v-bind:data="detailRecords"
           v-if="detailRecords.length > 0"
           v-bind:loading="loading || !companies.loaded"
           v-on:hook:mounted="mountTable"
           paginated
           pagination-simple
           ref="table"
           per-page="50"
           icon-pack="fas"
           v-bind:detailed="hasSpecificWhy"
           detail-key="company"
           show-detail-icon
           aria-next-label="Next page"
           aria-previous-label="Previous page"
           aria-page-label="Page"
           aria-current-label="Current page"
          >
            <b-table-column field="company.name" label="Company" sortable searchable>
              <template
                slot="searchable"
                slot-scope="props">
                  <b-input
                    v-model="props.filters[props.column.field]"
                    placeholder="Apple Inc."
                    size="is-small"
                  />
              </template>
              <template v-slot="props">
                {{ props.row.company.name }}
              </template>
            </b-table-column>
            <b-table-column field="company.primaryIdentifier.symbol" label="Symbol" sortable searchable>
              <template
                  slot="searchable"
                  slot-scope="props">
                  <b-input
                    v-model="props.filters[props.column.field]"
                    placeholder="AAPL"
                    size="is-small"
                  />
              </template>
              <template v-slot="props">
                {{ props.row.company.primaryIdentifier.symbol }}
              </template>
            </b-table-column>

            <template v-if="hasSpecificWhy">
              <b-table-column
                field="metric.specific_why"
                label="Explanation"
                sortable
                cell-class="truncated"
                v-slot="props"
              >
              <div class="modified-html-container">
                <div v-html="convertToSupertext(props.row.metric.specific_why)"></div>
              </div>
              </b-table-column>
            </template>

            <template v-if='!screen.is_binary'>
              <b-table-column field="metric.number" v-bind:label="`Value (${screen.unit})`" sortable numeric v-slot="props">
                {{ props.row.human_number }}
              </b-table-column>
              <b-table-column field="human_percentile" label="Percentile" sortable numeric v-slot="props">
                {{ ordinalSuffixOf(props.row.human_percentile) }}
              </b-table-column>
            </template>

            <b-table-column field="anyFlagged" v-bind:label="`Flagged for ${screen.is_inclusionary ? 'Inclusion' : 'Exclusion'}`" sortable v-slot="props">
              <FlagIcon
                v-bind:exclusion="props.row.exclusionFlagged"
                v-bind:inclusion="props.row.inclusionFlagged"
              />
            </b-table-column>

            <template slot="detail" slot-scope="props">
              <article class="media">
                <div class="media-content">
                  <div v-html="convertToSupertext(props.row.metric.specific_why)"></div>
                </div>
              </article>
            </template>

         </b-table>
        </section>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import Vue from 'vue';
import eventBus from '@frontend/shared/EventBus.js';
import {preciseRound} from '@frontend/shared/Util.js';
import {ordinal_suffix_of} from '@frontend/shared/Util.js';

import FlagIcon from '@frontend/shared/components/FlagIcon.vue';
import ScreenSwitch from '@frontend/builder/js/components/ScreenSwitch.vue';
import { Badge, StakeTooltip } from '@stakeui';
import gql from 'graphql-tag';
import papa from 'papaparse';

// One of MetricDescription.SOURCE_CHOICES in vampire/matchmaker/models.py: self-reported, no independent audit
const VOLUNTARY_SOURCE_TYPE = 'Company Reporting';

export default {
  data() {
    return {
      active: false,
      loading: true,
      prefilterFor: null,
      screen: {},
      ourMetrics: {},
      downloaded: false,
      isDownloading: false
    }
  },
  watch: {
    screen() {
      this.loading = true
      this.downloaded = false
      this.ourMetrics = {}
    }
  },
  apollo: {
    companyMetrics: {
      query() { 
        if(this.$root.advisor.hasFeature('Company Screener Bonds')) {
          return gql`query companyMetrics($companies:[ID],$metric_description:[ID]) {
            metrics(company_Id_In:$companies,metricDescription_Id_In:$metric_description,ticker_AssetType_In:["0","1"]) {
              edges {
                node {
                  company {
                    pk
                  }
                  number
                  percentile
                  specificWhy
                  benchmarkNumber
                  lessIsMore
                  unit
                }
              }
            }
          }
        `

        } else {
          return gql`query companyMetrics($companies:[ID],$metric_description:[ID]) {
            metrics(company_Id_In:$companies,metricDescription_Id_In:$metric_description,ticker_AssetType:"0") {
              edges {
                node {
                  company {
                    pk
                  }
                  number
                  percentile
                  specificWhy
                  benchmarkNumber
                  lessIsMore
                  isBinary
                  unit
                }
              }
            }
          }
        `
        }
    },
      variables() {
        const companies = this.companies.a.map((company)=> {
          return(company.pk)
        })
        return {metric_description:[this.screen.id],companies}
      },
      skip() {
        return(!this.companies.loaded || ! ('id' in this.screen))
      },
      update(data) {
        let ourMetrics = {}
        data.metrics.edges.forEach((edge)=> {
          let newNode = {}
            Object.keys(edge.node).forEach((key) => {
              newNode[key.replace(/[A-Z]/g,letter => `_${letter.toLowerCase()}`)] = edge.node[key]
            })

          ourMetrics[edge.node.company.pk] = newNode
        })
        this.ourMetrics = ourMetrics
        if(Object.keys(this.ourMetrics).length > 0) {
          this.screen.unit = this.ourMetrics[Object.keys(this.ourMetrics)[0]].unit
        }
        this.loading = false
      }
    }
  },
  props: {
    companies: {
      type: Object,
      default: function() {
        return {a: [], byId: {}}
      }
    }
  },
  components: {
    FlagIcon, ScreenSwitch, Badge, StakeTooltip,
  },
  methods: {
    convertToSupertext(text) {
      const regex = /\((https?:\/\/[^\s)]+)\)/g;
      let idx = 0;
      const replacedText = text.replace(regex, (match, url) => {
        idx += 1;
        return `<sup><a href="${url}" target="_blank" rel="noreferrer noopener">${idx}</a></sup>`;
      });
      return replacedText.replace("</a></sup><sup><a", ",</a></sup><sup><a");
    },
    mountTable: function(i, e, a) {
      if (this.prefilterFor) {
        Vue.set(this.$refs.table.filters, 'company.name', this.prefilterFor.name);
      }
    },
    ordinalSuffixOf: function(i) {
      return ordinal_suffix_of(i)
    },
    urls_to_supertext(text) {
      let idx = 0;

      function idxRepl(match) {
        idx++;
        const urlRegex = /(\((https?:\/\/[^)]+)\))/;
        const urlMatch = match.match(urlRegex);
        if (urlMatch && urlMatch[2]) {
          const url = urlMatch[2];
          return `<sup><a href="${url}" target="_blank" rel="noreferrer noopener">${idx}</a></sup>`;
        }
        return match;
      }

      const replacedText = text.replace(/\((https?:\/\/[^)]+)\)/g, idxRepl);
      return replacedText;
    },

    textContent(html) {
      if (!html) {
        return html;
      }

      let parser = new DOMParser();

      let parsedHTML = parser.parseFromString(html, 'text/html').body.innerHTML;
      let modifiedHTML = this.urls_to_supertext(parsedHTML);

      return modifiedHTML;
    },
    
    switchPercentile(evt, screen) {
      eventBus.$emit('switchPercentile', evt, screen);
    },
    sliderChange(evt, screen) {
      eventBus.$emit('screen-manually-set', evt, screen);
      eventBus.$emit('screens-changed', evt, screen);
    },
    switchText(screen) {
      if (screen.is_inclusionary) {
        return `Flag all companies associated with ${screen.name} for inclusion.`
      }
      else {
        return `Flag all companies associated with ${screen.name} for exclusion.`
      }
    },
    updatePercentile: function(newValue) {
      this.screen.userPercentile = newValue;
      this.emitChangeEvent();
    },
    emitChangeEvent: function(evt) {
      eventBus.$emit('screens-changed', this.screen);
    },
    downloadUniverse: function() {
      if(this.downloaded && this.isDownloading) {
        return
      }
      this.downloaded = true
      this.isDownloading = true
      let query = gql`
        query metricData($metricDescription:ID) {
          metrics(metricDescription_Id_In: [$metricDescription], ticker_AssetType:"0",number_Gt:0) {
            edges {
              node {
                company {
                  name
                  primaryIdentifier {
                    symbol
                  }
                  tickerSet(assetType_In:"0") {
                    edges {
                      node {
                        symbol
                      }
                    }
                  }
                }
                number
                specificWhy
              
              }
            }
          }
        }
      `
      this.$apollo.query({query,variables:{metricDescription:this.screen.id}}).then(
        (response) => {
          let csvFile = papa.unparse(response.data.metrics.edges.map((edge) => { 
            return {"Company Name":edge.node.company.name,
              "Primary Symbol":edge.node.company.primaryIdentifier.symbol,
              "Number":edge.node.number,
              "Specific Why":edge.node.specificWhy,
              "Symbol List":edge.node.company.tickerSet.edges.map((edge2) => {
                return edge2.node.symbol
              }).join(';')}
            }))
            csvFile = `"
          Copyright Stake PBC. All rights reserved. You may not use the information contained herein as the basis for any derivative product
          (including, without limitation, constructing, modifying, or maintaining any index or passively managed investment products)
          without the express written consent of Stake PBC.
          The information, data, analyses and opinions contained herein
          (1) include the proprietary information of Stake PBC, or third party content providers
          (2) may not be copied or redistributed, (3) do not constitute investment advice offered by Stake PBC
          (4) are provided solely for informational purposes
          (5) are not warranted to be timely, complete or accurate. Except to the extent otherwise specifically
              required by law, neither Stake PBC, nor its affiliates nor their third party content providers shall
              be responsible for any trading decisions, damages or other losses resulting from, or related to, this
              information, data, analyses, opinions or their use.
        "\n\n` + csvFile;

        var csvData = new Blob([csvFile], {type: 'text/csv;charset=utf-8;'});
        var ourUrl =  null;
        if (navigator.msSaveBlob)
        {
            ourUrl = navigator.msSaveBlob(csvData, 'metric.csv');
        }
        else
        {
            ourUrl = window.URL.createObjectURL(csvData);
        }

        var tLink = document.createElement('a');
        tLink.href = ourUrl;
        tLink.setAttribute('download', 'metric.csv');
        this.isDownloading = false
        tLink.click();          
      })
    },
  },
  computed: {
    isVoluntarySource: function() {
      return this.screen.source_type === VOLUNTARY_SOURCE_TYPE;
    },
    hasSpecificWhy: function() {
      return Object.values(this.ourMetrics).some((metric)=> {
        return(metric.specific_why.length > 0)
      })
    },
    canDownload: function() {
      return this.$apollo && this.$root.advisor && this.$root.advisor.hasFeature('Percentile Download')
    },
    detailRecords: function() {
      if(this.loading ||  !this.companies.loaded || !('id' in this.screen)) { 
        return []
      }
      let id = this.screen.id;
      let records = this.companies.a
        .filter((i) => { return i.pk in this.ourMetrics })
        .map((i) => {
          let thisMetric = this.ourMetrics[i.pk];
          const ret = {
            company: i,
            metric: thisMetric,
            human_percentile: preciseRound((thisMetric.percentile * 100),0),
            human_benchmark_number: Math.round(thisMetric.benchmark_number).toLocaleString(),
            human_number: Math.round(thisMetric.number).toLocaleString(),
            exclusionFlagged: (thisMetric.percentile * 100) < this.screen.userPercentile && !(thisMetric.less_is_more == false && thisMetric.is_binary == true),
            inclusionFlagged: (thisMetric.percentile * 100) >= this.screen.userPercentile && this.screen.userPercentile > 0 && thisMetric.less_is_more == false && thisMetric.is_binary == true,
          }
          ret.anyFlagged = ret.exclusionFlagged || ret.inclusionFlagged;
          return ret;
        })
        .sort((a, b) => {
          if (this.screen.is_binary) {
            if (a.anyFlagged && b.anyFlagged) {
              return a.name > b.name ? 1 : -1;
            }
            return a.anyFlagged ? -1 : 1;
          }
          else {
            return +a.human_percentile >= +b.human_percentile ? 1 : -1
          }
        });
      return records
    },
  },
  created: function() {
    var self = this;
    this.$on('close', () => {
      self.active = false;
      self.tableMounted = false;
      self.prefilterFor = null;
      self.screen = {};
      eventBus.$emit('ScreenDetailPane:close');
      if (this.$refs.table) {
        Vue.set(this.$refs.table.filters, 'company.primaryIdentifier.symbol', '');
      }
    });
    eventBus.$on('reveal-screen-detail-pane', (screen, prefilterCompany) => {
      if (this.active && this.screen && screen && this.screen.id === screen.id) {
        this.$emit('close');
        return;
      }
      this.prefilterFor = prefilterCompany;
      if (screen && screen.id !== this.screen.id) {
        this.screen = {};
        this.$nextTick(() => {
          this.screen = screen;
          this.active = true;
        });
      }
      else {
        this.active = false;
      }
    });
  }
}

</script>

<style lang="scss">
  .bottom.box {
    width: 70%;
    box-shadow: 0px -5px 17px 2px rgba(0,0,0,0.32);
    z-index: 500;
    position: fixed;
    transition: bottom 500ms;
    bottom: 0%;
    left: 50%;
    transform: translateX(-50%);
    bottom: -100%;
    height: 90%;

  }

  .active .bottom.box {
    bottom: 0%;
    overflow: scroll;
    display: block;
    z-index: 101;
  }

  .bottom.box.peek.full {
    bottom: 0%;
    overflow: scroll;
    max-height: 100%;
  }

  .bottom.box .caret {
    text-align: center;
    cursor: pointer;
  }

  .switch-container {
    .switch-widget {
      display: block;
      margin: 0 auto;
      text-align: center;

      .check {
        margin: 0 auto;
      }
    }
  }

  #detailedTable {
    td.truncated {
      max-width: 30ch;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .source-link-number {
    margin-left: 0.25em;
  }

  .self-reported {
    cursor: help;
    font-size: 0.75em;
  }
</style>
